import { api } from '@/api/client'

export interface LogFileMeta {
  id: string
  path: string
  name: string
  size: number
  mtime: string | null
  current?: boolean
}

export interface LogsMetaResponse {
  terminalId: number
  type: string
  num: string | number
  files: LogFileMeta[]
  levels: string[]
}

export interface LogExplorerEntry {
  id: string
  lineNo: number
  timestamp?: string | null
  level?: string | null
  module?: string | null
  message?: string
  raw?: string
  focus?: boolean
}

export interface LogsQueryResponse {
  file: { path: string; name: string }
  entries: LogExplorerEntry[]
  order: 'asc' | 'desc'
  cursor: string
  nextCursor: string | null
  hasMore: boolean
  pageStats: {
    returned: number
    errors: number
    warnings: number
    info?: number
    debug?: number
    skip: number
    truncated?: boolean
    matched?: number
    window?: number
  }
  /** Счётчики по хвосту файла (~12k строк) — стабильны при смене фильтра */
  windowCounts?: {
    total: number
    errors: number
    warnings: number
    info: number
    debug: number
    window: number
  } | null
  applied: {
    level: string | null
    q: string | null
    limit: number
    window: number
  }
}

export interface LogsContextResponse {
  file: { path: string }
  focusIndex: number
  entries: LogExplorerEntry[]
}

function qs(params: Record<string, string | number | undefined | null>) {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export function fetchLogsMeta(terminalId: string | number) {
  return api<LogsMetaResponse>(`/api/terminals/${terminalId}/logs/meta`)
}

export function queryLogs(
  terminalId: string | number,
  opts: {
    file?: string
    cursor?: string | null
    limit?: number
    level?: string
    q?: string
    order?: 'asc' | 'desc'
  },
) {
  return api<LogsQueryResponse>(
    `/api/terminals/${terminalId}/logs/query${qs({
      file: opts.file,
      cursor: opts.cursor || undefined,
      limit: opts.limit ?? 300,
      level: opts.level,
      q: opts.q,
      order: opts.order ?? 'desc',
    })}`,
  )
}

export function fetchLogContext(
  terminalId: string | number,
  opts: { file: string; lineNo?: number; before?: number; after?: number },
) {
  return api<LogsContextResponse>(
    `/api/terminals/${terminalId}/logs/context${qs({
      file: opts.file,
      lineNo: opts.lineNo,
      before: opts.before ?? 40,
      after: opts.after ?? 40,
    })}`,
  )
}

export async function downloadLogFile(
  terminalId: string | number,
  opts: { file?: string; level?: string; q?: string; maxLines?: number } = {},
) {
  const res = await fetch(
    `/api/terminals/${terminalId}/logs/download${qs({
      file: opts.file,
      level: opts.level,
      q: opts.q,
      maxLines: opts.maxLines,
    })}`,
    { credentials: 'include' },
  )
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error((data as { error?: string }).error || res.statusText)
  }
  const blob = await res.blob()
  const disposition = res.headers.get('Content-Disposition')
  const match = disposition?.match(/filename="?([^";\n]+)"?/)
  return { blob, filename: match?.[1] || `logs-${terminalId}.log` }
}

export function analyzeLogs(body: {
  terminalId: string | number
  mode?: 'selection' | 'errors' | 'tail'
  logs?: Array<{ timestamp?: string | null; level?: string | null; message?: string; raw?: string }>
  logText?: string
  file?: string
  maxLines?: number
}) {
  return api<{ analysis: string; sourceCount?: number; mode?: string }>('/api/analyze-logs', {
    method: 'POST',
    body,
  })
}

export function formatBytes(n: number) {
  if (!Number.isFinite(n) || n < 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
